import {
    createDiagnosticRedactor,
    createLogEventRedactor,
    createLogger,
    createRedactor,
    LogLevels,
    type LogLevelsString,
    type PexLogger,
} from '@pexip/logger';

import {
    type ConsoleCapture,
    capturedLevel,
    capturedSource,
    toCapturedLogEvent,
} from './capture';
import {createDisabledLogger} from './disabledLogger';
import {parsePolicy} from './parse';
import {type FrontendLogRecord, toFrontendRecord} from './record';

interface LoggingFactories {
    createRedactor: typeof createRedactor;
    createLogger: typeof createLogger;
}

interface LoggingOptions {
    policy: unknown;
    minLevel: LogLevelsString;
    /**
     * Whether events are kept in memory for `window.pexDebug` and downloads.
     * @defaultValue true
     */
    retainEvents?: boolean;
    /** Receives every redacted event as a record, when a host accepts them. */
    forward?: (record: FrontendLogRecord) => void;
    /**
     * The host's console capture. With `forward`, captured console calls at or
     * above `minLevel` are redacted like logger events and forwarded as
     * `console` records, and the logger's own console output is excluded from
     * capture.
     */
    capture?: ConsoleCapture;
    factories?: LoggingFactories;
}

/** Redaction for diagnostics sent to an error-reporting service. */
export interface DiagnosticRedaction {
    /**
     * A redacted, JSON-safe copy of `item`, with the same rules as log
     * events, or `undefined` when it cannot be redacted safely or redaction
     * is incomplete; the caller then drops the item.
     */
    redact(item: object): Record<string, unknown> | undefined;
    /**
     * Whether diagnostic output must be withheld for the rest of the process,
     * because a runtime pattern could not be registered or logging is
     * disabled.
     */
    isRedactionIncomplete(): boolean;
}

type LoggingInitialization =
    | {status: 'ready'; logger: PexLogger; diagnostics: DiagnosticRedaction}
    | {
          status: 'disabled';
          logger: PexLogger;
          diagnostics: DiagnosticRedaction;
      };

/** Withholds every item, for disabled logging. */
const withheldDiagnostics: DiagnosticRedaction = Object.freeze({
    redact: () => undefined,
    isRedactionIncomplete: () => true,
});

/**
 * Initializes logging using the supplied policy and logger factories.
 * Any initialization failure disables logging without retaining the error.
 *
 * @example
 * initializeLogging({
 *     policy: {replacement: '[REDACTED]', keys: ['token']},
 *     minLevel: 'info',
 * });
 * initializeLogging({policy, minLevel: 'debug', retainEvents: false, forward});
 */
export const initializeLogging = ({
    policy: policyInput,
    minLevel,
    retainEvents = true,
    forward,
    capture,
    factories = {createRedactor, createLogger},
}: LoggingOptions): LoggingInitialization => {
    try {
        const policy = parsePolicy(policyInput);
        const redactor = factories.createRedactor({
            defaultReplaceTo: policy.replacement,
            keysToRedact: policy.keys.map(key => [key, policy.replacement]),
        });

        const capturing = forward && capture ? {forward, capture} : undefined;
        const logger = factories.createLogger({
            fileName: 'infinity-connect',
            minLevel,
            redactor,
            ...(retainEvents ? {} : {maxLogBytes: 0, maxLogLines: 0}),
            ...(forward
                ? {
                      logEventSink: {
                          send: event => forward(toFrontendRecord(event)),
                      },
                  }
                : {}),
            ...(capturing
                ? {
                      consoleOutputGuard: (output: () => void) =>
                          capturing.capture.withoutCapture(output),
                  }
                : {}),
        });

        if (capturing) {
            const redactEvent = createLogEventRedactor({redactor});
            const minimum = LogLevels[minLevel];
            try {
                capturing.capture.attach(event => {
                    if (capturedLevel(event) < minimum) {
                        return;
                    }
                    capturing.forward(
                        toFrontendRecord(
                            redactEvent(toCapturedLogEvent(event)),
                            {source: capturedSource(event)},
                        ),
                    );
                });
            } catch {
                // Console capture is optional; the logger works without it.
            }
        }

        return {
            status: 'ready',
            logger,
            diagnostics: Object.freeze({
                redact: createDiagnosticRedactor({redactor}),
                isRedactionIncomplete: () => redactor.isRedactionIncomplete(),
            }),
        };
    } catch {
        return {
            status: 'disabled',
            logger: createDisabledLogger(),
            diagnostics: withheldDiagnostics,
        };
    }
};
